import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ImageIcon,
  ChevronDown,
  ChevronUp,
  Music,
  AlertCircle,
  Loader2,
  Check,
} from 'lucide-react';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { normalizeStatus } from '@/utils/episode-helpers';
import { formatRelativeDate } from '@/utils/dateFormat';
import { getFtpStreamUrl } from '@/services/api';
import { StatusBadge } from '@/components/StatusBadge';
import { AudioPlayer, AudioPlayerHandle } from '@/components/AudioPlayer';
import { MobileClipCard } from './MobileClipCard';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { useEpisodeRealtime } from '@/hooks/useEpisodeRealtime';
import { useEpisodeActions } from '@/hooks/useEpisodeActions';
import { usePlaybackController } from '@/hooks/usePlaybackController';
import './MobileEpisodeDetail.css';

// ── Workflow stepper config ──────────────────────────────────────
const WORKFLOW_STEPS = [
  { key: 'draft', label: 'Draft' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'audio', label: 'Audio' },
  { key: 'merge', label: 'Merge' },
  { key: 'publish', label: 'Publish' },
];

const getWorkflowStep = (status: string): number => {
  const map: Record<string, number> = {
    draft: 0,
    generating_scripts: 1, scripts_pending_review: 1, scripts_approved: 1,
    script_failed: 1,
    generating_audio: 2, audio_pending_review: 2, audio_approved: 2,
    audio_failed: 2,
    merging: 3, merged: 3,
    publishing: 4, published: 4, publishing_failed: 4,
    failed: -1,
  };
  return map[status] ?? 0;
};

const isFailedStatus = (status: string): boolean =>
  ['failed', 'script_failed', 'audio_failed', 'publishing_failed'].includes(status);

// ── Component ────────────────────────────────────────────────────

export const MobileEpisodeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userCompanies, getCompanyName, getCompanyColor } = useUserCompanies();
  const companyIds = useMemo(
    () => userCompanies.length > 0 ? userCompanies.map(c => c.id) : undefined,
    [userCompanies],
  );

  // Core state
  const [episode, setEpisode] = useState<PodcastEpisode | null>(null);
  const [clips, setClips] = useState<Clip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [clipsLoading, setClipsLoading] = useState(true);

  // UI state
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approveScripts' | 'approveAudio' | null>(null);

  // Merged audio
  const [mergedAudioUrl, setMergedAudioUrl] = useState<string | null>(null);
  const [mergedAudioLoading, setMergedAudioLoading] = useState(false);

  // Artwork
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);

  // Title/description setters for useEpisodeActions compatibility (unused in read-only)
  const [, setEditedTitle] = useState('');
  const [, setDescription] = useState('');

  // Refs
  const mergedAudioRef = useRef<AudioPlayerHandle>(null);
  const loadClipsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── Load clips ──────────────────────────────────────────────
  const loadClipsInternal = useCallback(async () => {
    if (!id) return;
    try {
      const clipsData = await PodcastEpisodeService.getClips(id);
      const sorted = [...clipsData].sort((a, b) => a.position - b.position);
      setClips(sorted);
    } catch (err) {
      console.error('Failed to load clips:', err);
    } finally {
      setClipsLoading(false);
    }
  }, [id]);

  const loadClips = useCallback(() => {
    if (loadClipsTimeoutRef.current) clearTimeout(loadClipsTimeoutRef.current);
    loadClipsTimeoutRef.current = setTimeout(() => {
      loadClipsInternal();
      loadClipsTimeoutRef.current = null;
    }, 2000);
  }, [loadClipsInternal]);

  useEffect(() => {
    return () => {
      if (loadClipsTimeoutRef.current) clearTimeout(loadClipsTimeoutRef.current);
    };
  }, []);

  // ── Realtime updates (WebSocket) ────────────────────────────
  const { setInitialStatus } = useEpisodeRealtime({
    episodeId: id,
    setEpisode,
    setClips,
    setError,
    loadClips,
    episodeTitle: episode?.title,
  });

  // ── Actions (only approve scripts/audio used) ───────────────
  const {
    actionInProgress,
    handleApproveScripts,
    handleApproveAudio,
  } = useEpisodeActions({
    episodeId: id,
    episode,
    clips,
    setEpisode,
    setClips,
    setError,
    setClipsLoading,
    loadClips,
    setEditedTitle,
    setDescription,
  });

  // ── Playback ────────────────────────────────────────────────
  const {
    currentlyPlayingId,
    handlePlayStateChange,
  } = usePlaybackController({ clips, mergePreview: episode?.mergePreview, mergedAudioRef });

  // ── Initial load ────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const loadEpisode = async () => {
      setLoading(true);
      try {
        const ep = await PodcastEpisodeService.getEpisode(id, companyIds);
        if (ep) {
          const normalizedStatus = normalizeStatus(ep.status as string);
          setEpisode({ ...ep, status: normalizedStatus });
          setInitialStatus(normalizedStatus);
          setEditedTitle(ep.title);
          setDescription(ep.description || '');
        } else {
          setError('Episode not found');
        }
      } catch (err) {
        console.error('Error fetching episode:', err);
        setError(err instanceof Error ? err.message : 'Failed to load episode');
      } finally {
        setLoading(false);
      }
    };
    loadEpisode();
    loadClipsInternal();
  }, [id, loadClipsInternal, companyIds, setInitialStatus]);

  // ── Merged audio URL ────────────────────────────────────────
  useEffect(() => {
    const mergedStatuses = [EpisodeStatus.MERGED, EpisodeStatus.PUBLISHING, EpisodeStatus.PUBLISHED];
    if (!episode || !mergedStatuses.includes(episode.status)) return;
    if (!episode.mergedAudioPath) return;

    setMergedAudioLoading(true);
    getFtpStreamUrl(episode.mergedAudioPath, episode.companyId)
      .then(setMergedAudioUrl)
      .catch(() => setMergedAudioUrl(null))
      .finally(() => setMergedAudioLoading(false));
  }, [episode]);

  // ── Artwork URL ─────────────────────────────────────────────
  useEffect(() => {
    if (!episode?.coverArtUrl) { setArtworkUrl(null); return; }
    if (episode.coverArtUrl.startsWith('http://') || episode.coverArtUrl.startsWith('https://')) {
      setArtworkUrl(episode.coverArtUrl);
      return;
    }
    getFtpStreamUrl(episode.coverArtUrl, episode.companyId)
      .then(setArtworkUrl)
      .catch(() => setArtworkUrl(null));
  }, [episode?.coverArtUrl, episode?.companyId]);

  // ── Approve handlers with inline confirmation ───────────────
  const handleConfirmApprove = async () => {
    if (confirmAction === 'approveScripts') {
      await handleApproveScripts();
    } else if (confirmAction === 'approveAudio') {
      await handleApproveAudio();
    }
    setConfirmAction(null);
  };

  // ── Derived state ───────────────────────────────────────────
  const showApproveScripts = episode?.status === EpisodeStatus.SCRIPTS_PENDING_REVIEW;
  const showApproveAudio = episode?.status === EpisodeStatus.AUDIO_PENDING_REVIEW;
  const showActionStrip = showApproveScripts || showApproveAudio;
  const currentStep = episode ? getWorkflowStep(episode.status) : 0;
  const isFailed = episode ? isFailedStatus(episode.status) : false;
  const showMergedAudio = episode && [EpisodeStatus.MERGED, EpisodeStatus.PUBLISHING, EpisodeStatus.PUBLISHED].includes(episode.status);
  const showClips = episode && ![EpisodeStatus.MERGED, EpisodeStatus.PUBLISHING, EpisodeStatus.PUBLISHED].includes(episode.status) && clips.length > 0;
  const companyName = episode ? getCompanyName(episode.companyId) : null;
  const companyColor = episode ? getCompanyColor(episode.companyId) : null;
  const isMultiCompany = userCompanies.length > 1;
  const descriptionNeedsExpand = episode?.description && episode.description.length > 150;

  // ── Loading skeleton ────────────────────────────────────────
  if (loading || clipsLoading) {
    return (
      <div className="pm_mdetail-skeleton">
        <div className="pm_mdetail-skeleton-header">
          <div className="pm_mdetail-skeleton-block" style={{ width: 36, height: 36 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="pm_mdetail-skeleton-block" style={{ width: '60%', height: 16 }} />
            <div className="pm_mdetail-skeleton-block" style={{ width: '35%', height: 12 }} />
          </div>
        </div>
        <div className="pm_mdetail-skeleton-content">
          <div className="pm_mdetail-skeleton-block" style={{ width: 160, height: 160, borderRadius: 12 }} />
          <div className="pm_mdetail-skeleton-block" style={{ width: '50%', height: 20 }} />
          <div className="pm_mdetail-skeleton-block" style={{ width: '100%', height: 80, borderRadius: 10 }} />
          <div className="pm_mdetail-skeleton-block" style={{ width: '100%', height: 60, borderRadius: 10 }} />
          <div className="pm_mdetail-skeleton-block" style={{ width: '100%', height: 60, borderRadius: 10 }} />
        </div>
      </div>
    );
  }

  // ── Error / not found ───────────────────────────────────────
  if (error || !episode) {
    return (
      <div className="pm_mdetail-root">
        <div className="pm_mdetail-header">
          <button className="pm_mdetail-back-btn" onClick={() => navigate('../episodes')}>
            <ArrowLeft size={20} />
          </button>
          <div className="pm_mdetail-header-info">
            <h1 className="pm_mdetail-header-title">Episode</h1>
          </div>
        </div>
        <div className="pm_mdetail-content">
          <div className="pm_mdetail-empty">
            <AlertCircle size={32} style={{ color: 'var(--pm-text-faint)' }} />
            <h2 className="pm_mdetail-empty-title">
              {error === 'Episode not found' ? 'Episode Not Found' : 'Something Went Wrong'}
            </h2>
            <p className="pm_mdetail-empty-sub">
              {error === 'Episode not found'
                ? 'This episode may have been deleted or you may not have access.'
                : error || 'Failed to load episode.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────
  return (
    <div className="pm_mdetail-root">
      {/* Header */}
      <div className="pm_mdetail-header">
        <button className="pm_mdetail-back-btn" onClick={() => navigate('../episodes')}>
          <ArrowLeft size={20} />
        </button>
        <div className="pm_mdetail-header-info">
          <h1 className="pm_mdetail-header-title">{episode.title}</h1>
          <p className="pm_mdetail-header-sub">
            Updated {formatRelativeDate(episode.updatedAt)}
          </p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="pm_mdetail-content">
        {/* Hero: artwork + title + badge */}
        <div className="pm_mdetail-hero">
          <div className="pm_mdetail-artwork">
            {artworkUrl ? (
              <img src={artworkUrl} alt={episode.title} />
            ) : (
              <ImageIcon size={40} className="pm_mdetail-artwork-placeholder" />
            )}
          </div>
          <div className="pm_mdetail-title-area">
            {isMultiCompany && companyName && (
              <span
                className="pm_mdetail-company-tag"
                style={{ backgroundColor: companyColor || '#4A90E2' }}
              >
                {companyName}
              </span>
            )}
            <h2 className="pm_mdetail-episode-title">{episode.title}</h2>
            <StatusBadge
              status={episode.status}
              scheduledPublishDate={episode.scheduledPublishDate}
              size="lg"
            />
          </div>
        </div>

        {/* Description */}
        {episode.description && (
          <div className="pm_mdetail-description">
            <p className={`pm_mdetail-description-text ${!descriptionExpanded && descriptionNeedsExpand ? 'collapsed' : ''}`}>
              {episode.description}
            </p>
            {descriptionNeedsExpand && (
              <button
                className="pm_mdetail-description-toggle"
                onClick={() => setDescriptionExpanded(prev => !prev)}
              >
                {descriptionExpanded
                  ? <><ChevronUp size={14} /> Show less</>
                  : <><ChevronDown size={14} /> Show more</>
                }
              </button>
            )}
          </div>
        )}

        {/* Error banner (e.g. failed status) */}
        {episode.errorMessage && (
          <div className="pm_mdetail-error">
            <AlertCircle size={16} className="pm_mdetail-error-icon" />
            <p className="pm_mdetail-error-text">{episode.errorMessage}</p>
          </div>
        )}

        {/* Workflow stepper */}
        <div className="pm_mdetail-stepper">
          <p className="pm_mdetail-stepper-label">Workflow</p>
          <div className="pm_mdetail-stepper-row">
            {WORKFLOW_STEPS.map((step, index) => {
              const isCompleted = currentStep > index || (currentStep === index && episode.status === EpisodeStatus.PUBLISHED);
              const isCurrent = currentStep === index && episode.status !== EpisodeStatus.PUBLISHED;

              let dotClass = 'pending';
              if (isCompleted) dotClass = 'completed';
              else if (isCurrent && isFailed) dotClass = 'failed';
              else if (isCurrent) dotClass = 'current';

              return (
                <div key={step.key} className="pm_mdetail-step">
                  <div className={`pm_mdetail-step-dot ${dotClass}`}>
                    {isCompleted ? <Check size={14} /> : index + 1}
                  </div>
                  <span className={`pm_mdetail-step-name ${isCurrent || isCompleted ? 'active' : ''}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Merged audio player */}
        {showMergedAudio && (
          <div className="pm_mdetail-merged-audio">
            <div className="pm_mdetail-merged-audio-header">
              <Music size={16} className="pm_mdetail-merged-audio-icon" />
              <p className="pm_mdetail-merged-audio-label">Final Episode</p>
            </div>
            {mergedAudioLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--pm-text-faint)' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--pm-text-faint)' }}>Loading audio...</span>
              </div>
            ) : mergedAudioUrl ? (
              <AudioPlayer src={mergedAudioUrl} ref={mergedAudioRef} />
            ) : (
              <p style={{ fontSize: '0.8125rem', color: 'var(--pm-text-faint)', margin: 0 }}>
                Audio not available
              </p>
            )}
          </div>
        )}

        {/* Clips list */}
        {showClips && (
          <div>
            <div className="pm_mdetail-clips-header">
              <h3 className="pm_mdetail-clips-title">Clips</h3>
              <span className="pm_mdetail-clips-count">{clips.length} clip{clips.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="pm_mdetail-clips-list">
              {clips.map((clip, index) => (
                <MobileClipCard
                  key={clip.id}
                  clip={clip}
                  position={index + 1}
                  isPlaying={currentlyPlayingId === clip.id}
                  onPlayStateChange={handlePlayStateChange}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom action strip: Approve Scripts / Approve Audio */}
      {showActionStrip && !confirmAction && (
        <div className="pm_mdetail-action-strip">
          <button
            className="pm_mdetail-approve-btn"
            onClick={() => setConfirmAction(showApproveScripts ? 'approveScripts' : 'approveAudio')}
          >
            <Check size={18} />
            {showApproveScripts ? 'Approve All Scripts' : 'Approve All Audio'}
          </button>
        </div>
      )}

      {/* Confirmation strip */}
      {confirmAction && (
        <div className="pm_mdetail-confirm-strip">
          <p className="pm_mdetail-confirm-text">
            {confirmAction === 'approveScripts'
              ? 'Approve all scripts and advance to audio generation?'
              : 'Approve all audio clips and advance to the next stage?'}
          </p>
          <div className="pm_mdetail-confirm-buttons">
            <button
              className="pm_mdetail-confirm-cancel"
              onClick={() => setConfirmAction(null)}
              disabled={!!actionInProgress}
            >
              Cancel
            </button>
            <button
              className="pm_mdetail-confirm-yes"
              onClick={handleConfirmApprove}
              disabled={!!actionInProgress}
            >
              {actionInProgress ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
                  Approving...
                </>
              ) : (
                'Confirm'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
